import { useMemo } from "react";

import { findSelectedPrice } from "@/lib/purchase-selections";
import type {
  LastPurchaseSummary,
  PriceComparison,
  PriceComparisonPrice,
} from "@/types";

export interface PurchaseRequestDecisionItemAlternative {
  supplierId: number;
  supplierName: string;
  currency: string;
  unitPriceOriginal: number | null;
  unitPriceArs: number | null;
  diffVsSelectedPct: number | null;
  isBestPrice: boolean;
  missingExchangeRate: boolean;
}

export interface PurchaseRequestDecisionItem {
  requestItemId: number;
  description: string;
  quantity: number;
  unit: string;
  selectedSupplierId: number;
  selectedSupplierName: string;
  selectedCurrency: string;
  selectedUnitPriceOriginal: number | null;
  selectedUnitPriceArs: number | null;
  selectedMissingExchangeRate: boolean;
  isBestSelected: boolean;
  alternatives: PurchaseRequestDecisionItemAlternative[];
  lastPurchase: LastPurchaseSummary | null;
}

export interface PurchaseRequestDecisionSupplierSummary {
  supplierId: number;
  supplierName: string;
  paymentMethod: string | null;
  paymentTermDays: string | null;
  deliveryTime: string | null;
  validityText: string | null;
  selectedItemsCount: number;
  selectedSubtotalArs: number;
}

export interface PurchaseRequestDecisionModel {
  items: PurchaseRequestDecisionItem[];
  selectedSuppliers: PurchaseRequestDecisionSupplierSummary[];
}

const byPriceAsc = (a: PriceComparisonPrice, b: PriceComparisonPrice) => {
  const aValue = a.unit_price_ars ?? Number.POSITIVE_INFINITY;
  const bValue = b.unit_price_ars ?? Number.POSITIVE_INFINITY;
  return aValue - bValue;
};

const calculateDiffPct = (base: number | null, value: number | null) => {
  if (base == null || base <= 0 || value == null) return null;
  const pct = ((value - base) / base) * 100;
  return Number.isFinite(pct) ? Number(pct.toFixed(1)) : null;
};

export function usePurchaseRequestDecisionItems(
  priceComparison: PriceComparison | null | undefined,
  lastPurchasesByRequestItemId: Record<number, LastPurchaseSummary> = {},
) {
  return useMemo<PurchaseRequestDecisionModel>(() => {
    if (!priceComparison?.rows?.length) {
      return { items: [], selectedSuppliers: [] };
    }

    const suppliersById = new Map(
      (priceComparison.headers?.suppliers ?? []).map((supplier) => [
        supplier.id,
        supplier,
      ]),
    );

    const items: PurchaseRequestDecisionItem[] = [];
    const selectedSupplierIds = new Set<number>();
    const selectedSupplierStats = new Map<
      number,
      { selectedItemsCount: number; selectedSubtotalArs: number }
    >();

    priceComparison.rows.forEach((row) => {
      const requestItemId = row.request_item.id;

      const availableOptions = row.prices
        .filter((price): price is PriceComparisonPrice => Boolean(price))
        .slice()
        .sort(byPriceAsc);

      const selected = findSelectedPrice(availableOptions);
      if (!selected?.supplier_id) return;

      selectedSupplierIds.add(selected.supplier_id);

      const selectedSupplier = suppliersById.get(selected.supplier_id);
      const selectedCurrency =
        selected.currency ?? selectedSupplier?.currency ?? "ARS";
      const selectedUnitPriceOriginal =
        selected.unit_price_original ??
        (selectedCurrency === "ARS" ? selected.unit_price_ars : null);
      const selectedUnitPriceArs = selected.unit_price_ars ?? null;

      const alternatives = availableOptions
        .filter((price) => price.supplier_id !== selected.supplier_id)
        .map((price) => {
          const supplier = suppliersById.get(price.supplier_id);
          const alternativeCurrency =
            price.currency ?? supplier?.currency ?? "ARS";
          const alternativeUnitPriceOriginal =
            price.unit_price_original ??
            (alternativeCurrency === "ARS" ? price.unit_price_ars : null);

          return {
            supplierId: price.supplier_id,
            supplierName:
              price.supplier_name ??
              supplier?.name ??
              `Proveedor ${price.supplier_id}`,
            currency: alternativeCurrency,
            unitPriceOriginal: alternativeUnitPriceOriginal,
            unitPriceArs: price.unit_price_ars ?? null,
            diffVsSelectedPct: calculateDiffPct(
              selectedUnitPriceArs,
              price.unit_price_ars ?? null,
            ),
            isBestPrice: Boolean(price.is_best_price),
            missingExchangeRate: Boolean(price.missing_exchange_rate),
          };
        });

      const previousStats = selectedSupplierStats.get(selected.supplier_id) ?? {
        selectedItemsCount: 0,
        selectedSubtotalArs: 0,
      };

      const lineTotalArs =
        selectedUnitPriceArs != null
          ? selectedUnitPriceArs * Number(row.request_item.quantity || 0)
          : 0;

      selectedSupplierStats.set(selected.supplier_id, {
        selectedItemsCount: previousStats.selectedItemsCount + 1,
        selectedSubtotalArs: previousStats.selectedSubtotalArs + lineTotalArs,
      });

      items.push({
        requestItemId,
        description: row.request_item.description,
        quantity: row.request_item.quantity,
        unit: row.request_item.unit,
        selectedSupplierId: selected.supplier_id,
        selectedSupplierName:
          selected.supplier_name ??
          selectedSupplier?.name ??
          `Proveedor ${selected.supplier_id}`,
        selectedCurrency,
        selectedUnitPriceOriginal,
        selectedUnitPriceArs,
        selectedMissingExchangeRate: Boolean(selected.missing_exchange_rate),
        isBestSelected: Boolean(selected.is_best_price),
        alternatives,
        lastPurchase: lastPurchasesByRequestItemId[requestItemId] ?? null,
      });
    });

    const selectedSuppliers = Array.from(selectedSupplierIds)
      .map((supplierId) => {
        const supplier = suppliersById.get(supplierId);
        if (!supplier) return null;

        return {
          supplierId: supplier.id,
          supplierName: supplier.name,
          paymentMethod: supplier.payment_method ?? null,
          paymentTermDays: supplier.payment_term_days ?? null,
          deliveryTime: supplier.delivery_time ?? null,
          validityText: supplier.validity_text ?? null,
          selectedItemsCount:
            selectedSupplierStats.get(supplier.id)?.selectedItemsCount ?? 0,
          selectedSubtotalArs:
            selectedSupplierStats.get(supplier.id)?.selectedSubtotalArs ?? 0,
        };
      })
      .filter((supplier): supplier is PurchaseRequestDecisionSupplierSummary =>
        Boolean(supplier),
      );

    return { items, selectedSuppliers };
  }, [priceComparison, lastPurchasesByRequestItemId]);
}
